import React from 'react';
import { UserOutlined } from '@ant-design/icons';
import { resolveMediaDisplayUrl } from '../utils/resolveMediaDisplayUrl';

interface UserAvatarProps {
  profileImage?: string | null;
  alt: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const sizeClasses = {
  sm: { container: 'h-8 w-8', text: 'text-xs', icon: 'text-sm' },
  md: { container: 'h-10 w-10', text: 'text-sm', icon: 'text-base' },
  lg: { container: 'h-16 w-16', text: 'text-xl', icon: 'text-2xl' },
};

const UserAvatar: React.FC<UserAvatarProps> = ({
  profileImage,
  alt,
  className = '',
  size = 'sm',
}) => {
  const initials = getInitials(alt);
  const s = sizeClasses[size];
  const resolvedImage = resolveMediaDisplayUrl(profileImage);

  return (
    <div
      className={`relative ${s.container} flex-shrink-0 overflow-hidden rounded-full ${className}`}
    >
      {resolvedImage ? (
        <img
          src={resolvedImage}
          alt={alt}
          className="h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
            const fallback = event.currentTarget.parentElement?.querySelector(
              '.user-avatar-fallback',
            ) as HTMLElement | null;
            fallback?.classList.remove('hidden');
          }}
        />
      ) : null}
      <div
        className={`user-avatar-fallback flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-700 dark:to-blue-800 ${
          resolvedImage ? 'hidden' : ''
        }`}
      >
        {initials ? (
          <span className={`${s.text} font-semibold text-blue-600 dark:text-blue-200`}>{initials}</span>
        ) : (
          <UserOutlined className={`${s.icon} text-blue-600 dark:text-blue-200`} />
        )}
      </div>
    </div>
  );
};

export default UserAvatar;
